import { Add, Remove } from '@mui/icons-material'
import { debounce } from 'lodash-es'
import { ReactElement, useCallback, useEffect, useState } from 'react'
import { ABSOLUTE_MAX_QUANTITY, Translate } from 'common'
import { roundQty } from 'cart'
import { useBannerPromotionStore } from '../../store/promotion/BannerPromotionStore'
import { timeout } from '../../theme/timeout'
import styled, { css } from 'styled-components'
import { Tooltip } from '../util/MUITooltipCustomization'

interface QuantitySelectorProps {
  orderIfNotAvailable: boolean
  minQty?: number
  maxQty?: number
  stepSize?: number
  onChange: (value: number) => Promise<void>
  onClick?: () => Promise<void>
  onRemove?: () => void
  onInputHoverTooltipMsg?: string
  orderNumber?: number
  isStoked?: boolean
  isLoading?: boolean
  disabled?: boolean
  value: string
  setValue: (val: string) => void
  isCondensedView?: boolean
  disablePlusStep?: boolean
  fullWidth?: boolean
}

export const QuantitySelector = (
  props: QuantitySelectorProps
): ReactElement => {
  const {
    onChange,
    minQty = 0,
    maxQty = ABSOLUTE_MAX_QUANTITY,
    stepSize = 1,
    onRemove = (): void => undefined,
    orderIfNotAvailable,
    onInputHoverTooltipMsg = '',
    orderNumber,
    isStoked,
    isLoading = false,
    disabled = false,
    value,
    setValue,
    isCondensedView,
    onClick,
    disablePlusStep,
    fullWidth,
  } = props

  const [internalValue, setInternalValue] = useState<string>(value || '')
  const [lastChangedValue, setlastChangedValue] = useState<string>(value)
  const [showTooltip, setShowTooltip] = useState<boolean>(false)

  const cursorType = isLoading ? 'wait' : 'pointer'

  useEffect(() => {
    if (value !== internalValue) {
      setInternalValue(value)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value])

  const handlePromotionBanner = () => {
    if (timeOutPromoDrawer) {
      clearInterval(timeOutPromoDrawer)
    }
    if (timeOutPromoDrawer) {
      if (timeOutPromoDrawerCart) {
        clearInterval(timeOutPromoDrawerCart)
      }
    }
    if (orderNumber) {
      handleCoverageMatch(orderNumber)
    }
    handleBannerPromoDrawerCartOpenExpanded()
  }

  const {
    handleCoverageMatch,
    timeOutPromoDrawer,
    timeOutPromoDrawerCart,
    handleBannerPromoDrawerCartOpenExpanded,
  } = useBannerPromotionStore()

  const minusStep = async () => {
    if (disabled) return
    function checkIfMinusStepIsLeadingToMinQty(val: string) {
      return Number(val) - 1 === minQty
    }
    handlePromotionBanner()
    const updatedVal = checkIfMinusStepIsLeadingToMinQty(value)
      ? minQty
      : Number(value) - stepSize
    if (updatedVal < minQty) {
      setShowTooltip(true)
      setTimeout(() => {
        setShowTooltip(false)
      }, timeout.minQtyNotification)
      if (onRemove) {
        onRemove()
      }
      return
    }
    setValue(`${updatedVal}`)
    await onChange(updatedVal)
  }

  const plusStep = async () => {
    if (disabled || disablePlusStep) return
    const updatedVal = Number(value) + stepSize
    handlePromotionBanner()
    if (
      (updatedVal > maxQty && !orderIfNotAvailable) ||
      updatedVal > ABSOLUTE_MAX_QUANTITY
    ) {
      return
    }
    setValue(`${updatedVal}`)
    await onChange(updatedVal)
  }

  const enforceRange = async (v: string) => {
    if (disabled) return
    if (!isValidInt(v)) {
      setInternalValue(`${lastChangedValue}`)
      setValue(`${lastChangedValue}`)
      return
    }
    let numericValue = Number.parseInt(v, 10)

    if (numericValue < minQty) {
      numericValue = minQty
    }
    if (numericValue > maxQty && !orderIfNotAvailable) {
      numericValue = maxQty
    }
    if (numericValue > ABSOLUTE_MAX_QUANTITY) {
      numericValue = ABSOLUTE_MAX_QUANTITY
    }
    handleRoundFigure.cancel()
    if (numericValue !== Number(value)) {
      await onChange(numericValue)
      setValue(`${numericValue}`)
    }
  }

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const handleRoundFigure = useCallback(
    debounce((v: string) => {
      if (isNaN(+v)) return

      let tempQty = Number(v)
      if (tempQty < minQty) {
        tempQty = minQty
      }

      const roundedValue = roundQty(tempQty, stepSize)
      const finalValue =
        roundedValue > maxQty && !orderIfNotAvailable ? maxQty : roundedValue

      setValue(finalValue.toString())
      onChange(Number(finalValue))
    }, 800),
    [stepSize, onChange, minQty]
  )

  const handleTypedInput = useCallback(
    (v: string) => {
      if (disabled) return

      if (v !== '') {
        setlastChangedValue(v)
      }
      if (v === '' || v === undefined) {
        setInternalValue('')
        return
      }

      if (!isValidInt(v)) {
        return
      }
      if (parseInt(v, 10) > ABSOLUTE_MAX_QUANTITY) {
        return
      }
      setValue(v)
      if (v !== internalValue) {
        setInternalValue(v)
      }
      handleRoundFigure(v)
    },
    [disabled, handleRoundFigure, internalValue, setValue]
  )

  const atMin = Number(value) === minQty
  const atMax = orderIfNotAvailable
    ? Number(value) === ABSOLUTE_MAX_QUANTITY
    : Number(value) === maxQty

  const tooltipMessage =
    minQty !== undefined
      ? Translate('theMinimumOrderQuantityIs', [`${minQty}`])
      : undefined

  const [inHover, setHover] = useState(false)

  const handleOnClick = async (actionCallback?: () => void): Promise<void> => {
    if (isLoading) return
    if (onClick) await onClick() // The onClick promise can potentially interrupt the flow if necessary.
    if (actionCallback) await actionCallback()
  }

  return (
    <Wrapper isCondensedView={isCondensedView} fullWidth={fullWidth}>
      <Tooltip
        title={<TooltipMessage>{tooltipMessage}</TooltipMessage>}
        open={showTooltip}
        arrow
      >
        <AddRemoveBtn
          onClick={() => handleOnClick(minusStep)}
          disabled={atMin || disabled}
          cursorType={cursorType}
          isCondensedView={isCondensedView}
        >
          <Remove />
        </AddRemoveBtn>
      </Tooltip>
      <FieldWrapper isStocked={isStoked}>
        <Tooltip
          title={<TooltipMessage>{onInputHoverTooltipMsg}</TooltipMessage>}
          open={Boolean(onInputHoverTooltipMsg) && inHover}
          placement="top"
          arrow
        >
          <Field
            value={Number(internalValue) > 0 ? internalValue : ''}
            onChange={(e) => handleTypedInput(e.target.value)}
            onClick={() => handleOnClick()}
            onInput={() => handleOnClick()}
            onBlur={(e) => {
              enforceRange(e.target.value)
            }}
            onMouseEnter={() => setHover(true)}
            onMouseLeave={() => {
              setHover(false)
            }}
            disabled={isLoading || disabled}
            isCondensedView={isCondensedView}
          />
        </Tooltip>
      </FieldWrapper>
      <AddRemoveBtn
        onClick={() => handleOnClick(plusStep)}
        disabled={atMax || disabled || disablePlusStep}
        cursorType={cursorType}
        isCondensedView={isCondensedView}
      >
        <Add />
      </AddRemoveBtn>
    </Wrapper>
  )
}

QuantitySelector.displayName = 'QuantitySelector'

interface WrapperProps {
  isCondensedView?: boolean
  fullWidth?: boolean
}

const Wrapper = styled.div<WrapperProps>`
  box-sizing: border-box;
  display: flex;
  border: 2px solid ${(p) => p.theme.colors.greyMedium};
  border-radius: 3px;
  min-width: ${(p) => (p.fullWidth ? '0px' : '168px')};
  max-width: ${(p) => (p.fullWidth ? '230px' : '168px')};
  margin: ${(p) => (p.isCondensedView ? '0px 2px' : '0px')};
  width: ${(p) => (p.fullWidth ? '100%' : '168px')};
`

interface AddRemoveBtnProps {
  disabled: boolean
  cursorType: string
  isCondensedView?: boolean
}

const AddRemoveBtn = styled.span<AddRemoveBtnProps>`
  display: flex;
  justify-content: center;
  align-items: center;
  width: ${(p) => (p.isCondensedView ? '24px' : '30px')};
  height: ${(p) => (p.isCondensedView ? '24px' : '30px')};
  color: ${(p) =>
    p.disabled ? p.theme.colors.disabledButtonText : p.theme.colors.text};
  background-color: ${(p) => p.theme.colors.greyMedium};
  cursor: ${(p) => (p.disabled ? 'not-allowed' : p.cursorType)};
`
interface FieldProps {
  isCondensedView?: boolean
}

const Field = styled.input<FieldProps>`
  width: 100%;
  height: ${(p) => (p.isCondensedView ? '24px' : '30px')};
  border: none;
  text-align: center;
`

interface IsStockedProp {
  isStocked?: boolean
}

const FieldWrapper = styled.span<IsStockedProp>`
  display: flex;
  flex: 1;
  min-width: 0;

  ${(p) =>
    p.isStocked &&
    css`
      outline: 4px solid ${p.theme.colors.blueLight};
      outline-offset: -4px;
    `}
`

const isValidInt = (n: string): boolean => {
  // Doesn't handle starting zeros. But minimum quantity will handle that.
  return /^\d+$/.test(n)
}

const TooltipMessage = styled.span`
  font-size: 16px;
`
